import { message } from "antd";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { useCallback, useMemo, useState } from "react";

import { useDict } from "@/hooks/useDict";
import { cn } from "@/lib/cn";

import { DetailRowEditControl } from "./DetailRowEditControl";
import type { DetailRowFieldType } from "./types";

export type { DetailRowFieldType } from "./types";

/** 双击复制时跳过的占位值 */
const COPY_SKIP_VALUES = new Set(["", "-", "—", "未关联"]);

/** 将 props value 转为字符串（用于编辑/复制） */
const toStringValue = (value: string | number | null | ReactNode): string => {
  if (value === null || value === undefined) return "";
  if (typeof value === "string" || typeof value === "number") return String(value);
  return "";
};

/** DetailRow 入参 */
export interface DetailRowProps {
  /** 左侧图标 */
  icon: LucideIcon;
  /**
   * 字段类型
   * - text（默认）：value 直接展示，不做额外处理
   * - dict：value 为字典项 code，展示与编辑走 dictCode
   */
  type?: DetailRowFieldType;
  /** 字段标签 */
  label: string;
  /** 字典类型编码；type 为 dict 时必填 */
  dictCode?: string;
  /** 字段值 */
  value: string | number | null | ReactNode;
  /** 点击值区域回调（如跳转关联账号） */
  onClick?: () => void;
  /** 是否支持双击复制，默认 true；isEdit 为 true 时双击改为编辑 */
  copyable?: boolean;
  /** 编辑模式：双击出现输入框或字典下拉 */
  isEdit?: boolean;
  /** 编辑确认后回调 */
  onEditChange?: (value: string) => void;
  /** 编辑控件占位 */
  placeholder?: string;
  /** 标签列宽度 class，默认 w-20 */
  labelClassName?: string;
}

/**
 * 详情弹窗单行字段：图标 + 标签 + 值
 * type=text：直接展示；type=dict：字典转义；isEdit 时双击编辑
 */
export const DetailRow = ({
  icon: Icon,
  type = "text",
  dictCode,
  label,
  value,
  onClick,
  copyable = true,
  isEdit = false,
  onEditChange,
  placeholder,
  labelClassName = "w-20",
}: DetailRowProps) => {
  const { getDictLabel } = useDict();
  const isDictField = type === "dict";
  // 当前是否处于行内编辑态
  const [editing, setEditing] = useState(false);
  // 编辑中的草稿值
  const [draft, setDraft] = useState("");
  const rawString = toStringValue(value);
  // 是否为可渲染的纯文本值
  const isPrimitiveValue =
    typeof value === "string" || typeof value === "number" || value === null || value === undefined;
  // 展示文案：text 直接展示，dict 走字典转义
  const displayText = useMemo(() => {
    if (isDictField) {
      if (!dictCode) return rawString || "—";
      return getDictLabel(dictCode, rawString);
    }
    if (!isPrimitiveValue) return "";
    return rawString || "—";
  }, [dictCode, getDictLabel, isDictField, isPrimitiveValue, rawString]);
  // 是否可编辑（字典/文本均为基础类型时可编辑）
  const canEditField = isEdit && isPrimitiveValue;
  // 是否可复制
  const canCopy =
    !isEdit &&
    copyable &&
    displayText !== "—" &&
    !COPY_SKIP_VALUES.has(displayText) &&
    !COPY_SKIP_VALUES.has(rawString);
  // 是否展示可点击链接样式
  const isClickable = Boolean(onClick) && canCopy;

  /** 提交编辑并退出输入态 */
  const commitEdit = useCallback(
    (nextValue?: string) => {
      const next = (nextValue ?? draft).trim();
      setEditing(false);
      if (next !== rawString) onEditChange?.(next);
    },
    [draft, onEditChange, rawString],
  );

  /** 取消编辑（已提交时不再回滚） */
  const cancelEdit = useCallback(() => {
    setEditing((cur) => {
      if (!cur) return cur;
      setDraft(rawString);
      return false;
    });
  }, [rawString]);

  /** 双击：编辑模式进入输入，否则复制 */
  const handleDoubleClick = useCallback(async () => {
    if (canEditField) {
      setDraft(rawString);
      setEditing(true);
      return;
    }
    if (!canCopy) return;
    const copyText = isDictField ? displayText : isPrimitiveValue ? rawString : displayText;
    try {
      await navigator.clipboard.writeText(copyText);
      message.success("已复制");
    } catch {
      message.error("复制失败");
    }
  }, [canCopy, canEditField, displayText, isDictField, isPrimitiveValue, rawString]);

  /** 只读展示内容 */
  const readOnlyContent = isPrimitiveValue ? displayText : value;

  const valueNode = editing ? (
    <DetailRowEditControl
      type={type}
      label={label}
      dictCode={dictCode}
      draft={draft}
      placeholder={placeholder}
      onDraftChange={setDraft}
      onCommit={commitEdit}
      onCancel={cancelEdit}
    />
  ) : isClickable ? (
    <button
      type="button"
      className="text-xs text-orange-600 hover:underline cursor-pointer text-left truncate"
      onClick={onClick}
      onDoubleClick={handleDoubleClick}
    >
      {readOnlyContent}
    </button>
  ) : (
    <span
      className={cn(
        "text-xs text-slate-800 truncate",
        canCopy && "cursor-copy select-text",
        canEditField && "cursor-text rounded px-0.5 -mx-0.5 hover:bg-orange-50/60",
      )}
      onDoubleClick={handleDoubleClick}
    >
      {readOnlyContent}
    </span>
  );

  return (
    <div className="flex items-center gap-2 py-1 min-w-0 flex-1">
      <Icon className="w-3.5 h-3.5 text-slate-400 shrink-0" />
      <span className={cn("text-xs text-slate-500 shrink-0", labelClassName)}>{label}</span>
      <div className="flex-1 min-w-0">{valueNode}</div>
    </div>
  );
};
